import "./Scoreboard.css";

import { SyntheticEvent } from "react";
import { useTranslation } from "react-i18next";

import {
  ISoccerMatch,
  PERIOD_ID,
  PERIOD_ID_STATUS,
  SoccerMatchStatus,
} from "../../../../../../types";
import {
  appTournamentConfig,
  TOURNAMENT_ID,
} from "../../../../config/tournament";
import { getTeamInfo, useMatchResult } from "../../../../hooks";

export const ScoreBoard = ({ soccerMatch }: { soccerMatch: ISoccerMatch }) => {
  const { finalScoreboard, isMatchPlaying, isMatchEnded } = useMatchResult({
    soccerMatch,
  });
  const { t } = useTranslation();

  const isClubTournament = appTournamentConfig[TOURNAMENT_ID]?.isClubTournament;
  const homeContestant = soccerMatch.contestants[0];
  const awayContestant = soccerMatch.contestants[1];
  const isHomeTBD = !homeContestant?.id;
  const isAwayTBD = !awayContestant?.id;
  const homeTeamInfo = getTeamInfo(homeContestant?.name ?? "");
  const awayTeamInfo = getTeamInfo(awayContestant?.name ?? "");

  const homeTeam = {
    logoImgSrc: homeTeamInfo.imgSrc,
    teamName: isHomeTBD
      ? "TBD"
      : isClubTournament
        ? homeTeamInfo.translatedAbbrName
        : homeTeamInfo.translatedName,
    score: finalScoreboard.home,
    isTBD: isHomeTBD,
  };
  const awayTeam = {
    logoImgSrc: awayTeamInfo.imgSrc,
    teamName: isAwayTBD
      ? "TBD"
      : isClubTournament
        ? awayTeamInfo.translatedAbbrName
        : awayTeamInfo.translatedName,
    score: finalScoreboard.away,
    isTBD: isAwayTBD,
  };

  const showScoreboard = isMatchPlaying || isMatchEnded;

  const handleLogoLoadError = (
    event: SyntheticEvent<HTMLImageElement, Event>,
  ) => {
    event.currentTarget.src = "/images/teamLogos/flag-fallback.png";
  };

  const {
    matchTime = -1,
    matchLengthSec = 0,
    periodId = -1,
  } = soccerMatch.matchDetails;

  const getTimeDisplayText = () => {
    const matchStatus = soccerMatch?.status;

    // Match hasn’t started or is not playable
    const isInactiveStatus = [
      SoccerMatchStatus.FIXTURE,
      SoccerMatchStatus.CANCELLED,
      SoccerMatchStatus.POSTPONED,
      SoccerMatchStatus.SUSPENDED,
    ].includes(matchStatus);

    if (isInactiveStatus) return "";

    const period = PERIOD_ID[periodId as keyof typeof PERIOD_ID] ?? "";

    // Show current match time if available
    if (matchTime && matchTime !== -1) {
      // Extra time logic
      const isFirstHalf = period === PERIOD_ID_STATUS.FIRST_HALF;
      const isSecondHalf = period === PERIOD_ID_STATUS.SECOND_HALF;

      const sec = String(matchLengthSec).padStart(2, "0");

      // Extra time computation for both halves
      if (isFirstHalf || isSecondHalf) {
        const maxTime = isFirstHalf ? 45 : 90;
        const regularTime = Math.min(matchTime, maxTime);
        const extraTime = matchTime > maxTime ? matchTime - maxTime : 0;

        return extraTime
          ? `${regularTime}+${extraTime}:${sec}`
          : `${regularTime}:${sec}`;
      }

      return `${matchTime}:${sec}`;
    }

    // Match status
    switch (period) {
      case PERIOD_ID_STATUS.HALF_TIME:
        return t("gameStatus.halfTime");
      case PERIOD_ID_STATUS.FULL_TIME:
        return "";
      default:
        return "";
    }
  };

  const gameTime = getTimeDisplayText();

  return (
    <div className="scoreboard-container">
      <div className="scoreboard-team-info">
        {homeTeam.isTBD ? (
          <img
            src="/images/teamLogos/flag-fallback.png"
            alt="TBD"
            height={41}
            width={68}
            className="scoreboard-team-flag"
          />
        ) : (
          <img
            src={homeTeam.logoImgSrc}
            alt={homeTeam.teamName}
            height={48}
            width={64}
            className={`scoreboard-team-flag ${
              isClubTournament ? "club-team-flag" : ""
            }`}
            onError={handleLogoLoadError}
          />
        )}
        <span className="scoreboard-team-name">{homeTeam.teamName}</span>
      </div>

      <div
        className={`scoreboard ${
          gameTime ? "scoreboard--with-final-text" : ""
        }`}
      >
        {showScoreboard ? (
          <>
            <span className="team-score">{homeTeam.score ?? "-"}</span>
            <span className="scoreboard-score-seperator">:</span>
            <span className="team-score">{awayTeam.score ?? "-"}</span>
          </>
        ) : (
          <span>V</span>
        )}
      </div>
      <div className="scoreboard-team-info">
        {awayTeam.isTBD ? (
          <img
            src="/images/teamLogos/flag-fallback.png"
            alt="TBD"
            height={41}
            width={68}
            className="scoreboard-team-flag"
          />
        ) : (
          <img
            src={awayTeam.logoImgSrc}
            alt={awayTeam.teamName}
            height={48}
            width={64}
            className={`scoreboard-team-flag ${
              isClubTournament ? "club-team-flag" : ""
            }`}
            onError={handleLogoLoadError}
          />
        )}
        <span className="scoreboard-team-name">{awayTeam.teamName}</span>
      </div>
    </div>
  );
};
